import React, { useEffect } from 'react';

import { Input } from './input';
import { Button } from './button';
import { X } from 'lucide-react';
import { cn } from '../../utils';

interface TagInputProps extends React.InputHTMLAttributes<HTMLInputElement> {
  placeholder?: string;
  inputValueRef?: React.MutableRefObject<string | undefined>;
  tags: string[];
  setTags: React.Dispatch<React.SetStateAction<string[]>>;
  maxItems?: number;
  tagClassName?: string;
  allowDuplicates?: boolean;
}

const TagInput = React.forwardRef<HTMLInputElement, TagInputProps>(
  (props, ref) => {
    const {
      placeholder,
      inputValueRef,
      tags,
      setTags,
      className,
      maxLength = 20,
      maxItems = 50,
      tagClassName,
      allowDuplicates = false,
    } = props;
    const [inputValue, setInputValue] = React.useState('');
    const [error, setError] = React.useState<string | null>(null);
    const inputRef = React.useRef<HTMLInputElement>(null);

    useEffect(() => {
      if (inputValueRef) {
        inputValueRef.current = inputValue;
      }
    }, [inputValue, inputValueRef]);

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      if (tags.length >= maxItems) {
        setError('Max. number of items added.');
        return;
      }

      if (e.target.value.length >= maxLength) {
        setInputValue(e.target.value.substring(0, maxLength));
        setError(`Character limit exceeded (${maxLength} max)`);
      } else {
        setInputValue(e.target.value);
        setError(null);
      }
    };

    const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (e.key === 'Enter' || e.key === ',') {
        e.preventDefault();
        const newTag = inputValue.trim();
        const isDuplicate = tags.includes(newTag);

        if (newTag && !isDuplicate) {
          setTags([...tags, newTag]);
          setInputValue('');
        }

        if (tags.length < maxItems) {
          setError(null);
        }

        if (isDuplicate && !allowDuplicates) {
          setError('Item already in the list.');
        }
      }
    };

    const removeTag = (tagToRemove: string) => {
      const filteredTags = tags.filter((tag) => tag !== tagToRemove);

      if (filteredTags.length < maxItems) {
        setError(null);
      }

      setTags(tags.filter((tag) => tag !== tagToRemove));
    };

    return (
      <div className="w-full">
        <Input
          ref={inputRef}
          type="text"
          placeholder={placeholder}
          value={inputValue}
          onChange={handleInputChange}
          onKeyDown={handleKeyDown}
          className={className}
          maxLength={maxLength}
        />
        {error && <p className="text-xs text-destructive mt-2">{error}</p>}
        <div
          className={`flex flex-wrap gap-2 rounded-md ${
            tags.length !== 0 && 'mt-3'
          }`}
        >
          {tags.map((tag, index) => (
            <span
              key={index}
              className={`transition-all border inline-flex h-8 items-center text-sm pl-3 rounded-full bg-card hover:bg-muted text-foreground ${tagClassName}`}
            >
              {tag}
              <Button
                type="button"
                variant="ghost"
                onClick={() => removeTag(tag)}
                className={cn(
                  'py-1 px-3 h-full hover:bg-transparent hover:text-foreground rounded-full'
                )}
              >
                <X size={14} />
              </Button>
            </span>
          ))}
        </div>
      </div>
    );
  }
);

TagInput.displayName = 'TagInput';

export { TagInput };
